import { useState, type ReactNode } from 'react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeftRight,
  BedDouble,
  CalendarClock,
  FileText,
  FolderOpen,
  HandCoins,
  LayoutDashboard,
  LogOut,
  MoreHorizontal,
  Package,
  PhoneCall,
  Printer,
  Receipt,
  ReceiptText,
  ScrollText,
  ShieldCheck,
  Stethoscope,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { tr } from '@/lib/i18n-core';
import { describeError, useAuth } from '@/lib/auth';
import { money } from '@/lib/utils';
import { ActionMenu, ActionMenuItem, Alert, Button, PageSkeleton } from '@/components/ui/primitives';
import { ClinicalShell } from '@/components/clinical-shell/ClinicalShell';
import { ClinicalNav, type ClinicalNavGroup, type ClinicalNavItem } from '@/components/clinical-shell/ClinicalNav';
import { ShellSectionHeading } from '@/components/clinical-shell/ShellPanel';
import { withSection } from '@/components/clinical-shell/section-param';
import { useStickyShell } from '@/components/clinical-shell/use-sticky-shell';
import { AdmissionAdvances } from '../../billing/AdmissionAdvances';
import { PatientPackagesCard, usePatientPackages } from '../../billing/PatientPackagesCard';
import { WardChargePanel } from '../WardChargePanel';
import { AdmissionPatientBar } from './AdmissionPatientBar';
import { admissionKey, useAdmission, useAdmissionLive, useCareTeam, usePreAuthStatus } from './queries';
import { admissionIsOpen, admissionPath, ipdEmrPath, type Admission } from './types';
import { WORKSPACE_GROUPS, WORKSPACE_HOME, groupOfSection, workspaceSectionIds, workspaceTarget, type WorkspaceSectionId } from './workspace-sections';
import { WorkspaceOverview } from './sections/WorkspaceOverview';
import { BedSection, TransferDialog, transfersKey } from './sections/BedSection';
import { CareTeamSection } from './sections/CareTeamSection';
import { BillSection, runningBillKey } from './sections/BillSection';
import { DuesSection } from './sections/DuesSection';
import { PreAuthSection } from './sections/PreAuthSection';
import { preAuthState } from './workspace-facts';
import { DocumentsSection, useAdmissionPrints } from './sections/DocumentsSection';
import { DischargeSection } from './sections/DischargeSection';

const SECTION_ICONS: Record<WorkspaceSectionId, LucideIcon> = {
  overview: LayoutDashboard,
  bed: BedDouble,
  'care-team': Users,
  bill: ReceiptText,
  'daily-charges': CalendarClock,
  advances: HandCoins,
  packages: Package,
  dues: PhoneCall,
  preauth: ShieldCheck,
  documents: ScrollText,
  discharge: LogOut,
};

export function AdmissionWorkspace() {
  const { admissionId = '' } = useParams<{ admissionId: string }>();
  const [params] = useSearchParams();
  const { can, hasModule } = useAuth();
  const admission = useAdmission(admissionId);
  const team = useCareTeam(admissionId);
  useAdmissionLive(admissionId);
  const insurance = hasModule('insurance');
  const billing = hasModule('billing');
  const preAuth = usePreAuthStatus(admissionId, insurance);
  const stayPackages = usePatientPackages({ patientId: '', admissionId }, Boolean(admissionId) && billing);
  const available = workspaceSectionIds({
    advances: can('deposit.manage', 'invoice.read'),
    dailyCharges: can('ward.charge.manage', 'admission.read'),
    packages: billing,
    dues: can('dues.read'),
    insurance,
  });
  const target = workspaceTarget(params, available);

  if (target.kind === 'emr') return <Navigate to={ipdEmrPath(admissionId, target.section)} replace />;
  if (admission.isLoading) return <PageSkeleton />;
  if (!admission.data) {
    return <Alert variant="destructive">{describeError(admission.error) || tr('ipd', 'that_admission_could_not_be_opened')}</Alert>;
  }

  return (
    <Workspace
      admission={admission.data}
      active={target.section}
      available={available}
      team={team}
      preAuth={preAuth}
      packages={billing ? (stayPackages.data?.data ?? undefined) : undefined}
      insurance={insurance}
    />
  );
}

function Workspace({
  admission,
  active,
  available,
  team,
  preAuth,
  packages,
  insurance,
}: {
  admission: Admission;
  active: WorkspaceSectionId;
  available: WorkspaceSectionId[];
  team: ReturnType<typeof useCareTeam>;
  preAuth: ReturnType<typeof usePreAuthStatus>;
  packages: Parameters<typeof WorkspaceOverview>[0]['packages'];
  insurance: boolean;
}) {
  const [, setParams] = useSearchParams();
  const queryClient = useQueryClient();
  const shell = useStickyShell();
  const current = admissionIsOpen(admission);
  const hasStayPackage = (packages ?? []).some((row) => row.status !== 'cancelled');

  const open = (id: string) => {
    setParams((params) => withSection(params, id, WORKSPACE_HOME), { replace: true });
    shell.scrollToTop();
  };

  const groups = navGroups(admission, available, {
    teamSize: team.data?.members.length ?? 0,
    preAuth: insurance ? preAuthState(admission, preAuth.data) : null,
    packages: (packages ?? []).filter((row) => row.status !== 'cancelled').length,
  });

  const refreshMoney = () => {
    void queryClient.invalidateQueries({ queryKey: runningBillKey(admission.id) });
    void queryClient.invalidateQueries({ queryKey: admissionKey(admission.id) });
  };

  const body: Record<WorkspaceSectionId, () => ReactNode> = {
    overview: () => (
      <WorkspaceOverview admission={admission} team={team.data} preAuth={preAuth.data} insurance={insurance} packages={packages} available={available} onOpen={open} />
    ),
    bed: () => <BedSection admission={admission} />,
    'care-team': () => <CareTeamSection admission={admission} team={team.data} loading={team.isLoading} />,
    bill: () => <BillSection admission={admission} hasStayPackage={hasStayPackage} />,
    'daily-charges': () => (
      <>
        <ShellSectionHeading icon={CalendarClock} title={tr('ipd', 'daily_charges')} description={tr('ipd', 'ws_daily_charges_desc')} />
        <WardChargePanel admissionId={admission.id} admitted={current} />
      </>
    ),
    advances: () => (
      <>
        <ShellSectionHeading icon={HandCoins} title={tr('ipd', 'advances')} description={tr('ipd', 'ws_advances_desc')} />
        <AdmissionAdvances admissionId={admission.id} admitted={current} />
      </>
    ),
    packages: () => (
      <>
        <ShellSectionHeading icon={Package} title={tr('ipd', 'ws_nav_packages')} description={tr('ipd', 'ws_packages_desc')} />
        <PatientPackagesCard
          patientId={admission.patient.id}
          admissionId={admission.id}
          sellKind={current ? 'ipd_stay' : undefined}
          title={tr('billing', 'inpatient_package')}
          onChanged={refreshMoney}
        />
      </>
    ),
    dues: () => <DuesSection admission={admission} />,
    preauth: () => <PreAuthSection admission={admission} status={preAuth.data} loading={preAuth.isLoading} />,
    documents: () => <DocumentsSection admission={admission} />,
    discharge: () => <DischargeSection admission={admission} />,
  };

  const preAuthWarning = current && preAuth.data?.required && !preAuth.data.satisfied && preAuth.data.message;

  return (
    <ClinicalShell
      shell={shell}
      activeKey={active}
      bar={
        <AdmissionPatientBar
          ref={shell.attachBar}
          admission={admission}
          team={team.data}
          onOpenCareTeam={available.includes('care-team') ? () => open('care-team') : undefined}
          actions={
            <>
              <MoreActions admission={admission} onOpen={open} />
              <Link to={ipdEmrPath(admission.id)} className="flex-1 sm:flex-none">
                <Button className="w-full shadow-md shadow-primary/25">
                  <Stethoscope className="h-4 w-4" aria-hidden /> {tr('ipd', 'ws_open_ipd_emr')}
                </Button>
              </Link>
            </>
          }
        />
      }
      banners={
        <>
          {preAuthWarning && (
            <Alert variant="warning" title={tr('ipd', 'pre_authorisation_needed')}>
              {preAuth.data?.message}{' '}
              {available.includes('preauth') && (
                <button type="button" className="font-medium underline underline-offset-2" onClick={() => open('preauth')}>
                  {tr('ipd', 'ws_see_pre_authorisation')}
                </button>
              )}
            </Alert>
          )}
          {admission.status === 'absconded' && admission.account.depositedMinor > 0 && active !== 'dues' && (
            <Alert variant="warning">
              {tr('ipd', 'this_patient_left_without_notice')} {money(admission.account.depositedMinor)} {tr('ipd', 'of_their_advance_was_not_refunded_it_is_kept_as')}
            </Alert>
          )}
        </>
      }
      nav={<ClinicalNav groups={groups} active={active} onSelect={open} labels={{ menu: tr('ipd', 'ws_menu'), done: tr('ipd', 'ws_marker_done'), required: tr('ipd', 'ws_marker_required') }} />}
    >
      {body[active]()}
    </ClinicalShell>
  );
}

function navGroups(
  admission: Admission,
  available: readonly WorkspaceSectionId[],
  facts: { teamSize: number; preAuth: ReturnType<typeof preAuthState> | null; packages: number },
): ClinicalNavGroup[] {
  const owing = admission.account.balanceMinor < 0;
  const openWork = admission.openWork ? admission.openWork.labItems + admission.openWork.radiology + admission.openWork.dueDoses : 0;
  const decorate = (id: WorkspaceSectionId): ClinicalNavItem => {
    const item: ClinicalNavItem = { id, label: tr('ipd', `ws_nav_${id.replace('-', '_')}`), icon: SECTION_ICONS[id] };
    if (id === 'care-team' && facts.teamSize > 0) return { ...item, count: facts.teamSize };
    if (id === 'bill' && owing) return { ...item, badge: { label: tr('ipd', 'owing'), tone: 'destructive' } };
    if (id === 'packages' && facts.packages > 0) return { ...item, count: facts.packages };
    if (id === 'preauth' && (facts.preAuth === 'needed' || facts.preAuth === 'pending')) {
      return { ...item, badge: { label: tr('ipd', `ws_preauth_${facts.preAuth}`), tone: facts.preAuth === 'needed' ? 'destructive' : 'info' } };
    }
    if (id === 'discharge' && admissionIsOpen(admission) && openWork > 0) return { ...item, count: openWork };
    return item;
  };
  return WORKSPACE_GROUPS.map((group) => ({
    id: group,
    label: tr('ipd', `ws_group_${group}`),
    items: available.filter((id) => groupOfSection(id) === group).map(decorate),
  }));
}

function MoreActions({ admission, onOpen }: { admission: Admission; onOpen: (id: string) => void }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const { printDoc, deathCertificate } = useAdmissionPrints(admission.id);
  const [transferring, setTransferring] = useState(false);
  const current = admissionIsOpen(admission);

  return (
    <>
      <ActionMenu
        label={tr('ipd', 'ws_more_actions')}
        trigger={
          <Button variant="outline">
            <MoreHorizontal className="h-4 w-4" aria-hidden /> {tr('ipd', 'ws_more_actions')}
          </Button>
        }
      >
        <ActionMenuItem icon={<FolderOpen />} onSelect={() => navigate(`/patients/${admission.patient.id}`)}>
          {tr('ipd', 'full_record')}
        </ActionMenuItem>
        {can('invoice.read') && (
          <ActionMenuItem icon={<Receipt />} onSelect={() => navigate(`/billing/statement?admissionId=${admission.id}`)}>
            {tr('ipd', 'statement')}
          </ActionMenuItem>
        )}
        <ActionMenuItem icon={<Printer />} onSelect={() => printDoc.mutate('bill')}>
          {current ? tr('ipd', 'ws_print_interim_bill') : tr('ipd', 'ws_print_final_bill')}
        </ActionMenuItem>
        {current && can('admission.manage', 'bed.manage') && (
          <ActionMenuItem icon={<ArrowLeftRight />} onSelect={() => setTransferring(true)}>
            {tr('ipd', 'transfer_bed')}
          </ActionMenuItem>
        )}
        {!current && (
          <ActionMenuItem icon={<FileText />} onSelect={() => printDoc.mutate('summary')}>
            {tr('ipd', 'discharge_summary')}
          </ActionMenuItem>
        )}
        {admission.status === 'expired' && can('certificate.issue') && (
          <ActionMenuItem icon={<ScrollText />} onSelect={() => deathCertificate.mutate()}>
            {tr('ipd', 'death_certificate')}
          </ActionMenuItem>
        )}
        {current && can('admission.manage') && (
          <ActionMenuItem
            icon={<LogOut />}
            onSelect={() => navigate(`/ipd/admissions/${admission.id}/discharge?returnTo=${encodeURIComponent(admissionPath(admission.id, 'discharge'))}`)}
          >
            {tr('ipd', 'discharge')}
          </ActionMenuItem>
        )}
        <ActionMenuItem icon={<ScrollText />} onSelect={() => onOpen('documents')}>
          {tr('ipd', 'ws_nav_documents')}
        </ActionMenuItem>
      </ActionMenu>
      {transferring && (
        <TransferDialog
          admissionId={admission.id}
          onClose={() => setTransferring(false)}
          onSaved={() => {
            setTransferring(false);
            void queryClient.invalidateQueries({ queryKey: admissionKey(admission.id) });
            void queryClient.invalidateQueries({ queryKey: transfersKey(admission.id) });
            void queryClient.invalidateQueries({ queryKey: ['bed-board'] });
          }}
        />
      )}
    </>
  );
}
